import React, { useState } from 'react';
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalCloseButton,
  Text,
  Box,
  ModalFooter,
  Flex,
  Button,
  useDisclosure,
} from '@chakra-ui/core';
import { useTranslation } from 'react-i18next';
import colors from '../../styles/colors';
import { ReviewQuestions } from '../administration/topCandidates/reviewQuestions/ReviewQuestions';
import { InReviewQuestionListType } from '../../firebase/firestore/documents/candidate';
import { useStoreActions, useStoreState } from '../../models/hooks';
import { QuestionFormdataType } from '../messaging/seeker/candidate/questions/QuestionType';
import { DismissConfirmationModal } from '../administration/topCandidates/DismissConfirmationModal';

export type ReviewPausedQuestionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  selectedToDoItem?: {
    id: string;
    task: string;
    candidateName: string;
    candidateId: string;
    helpRequestedMessages?: {
      userType: string;
      message: string;
      timestamp: Date;
      name: string;
    }[];
    latestHelpRequestFor?: string;
    seekerHireDetailId?: string;
    hireDetailsRequestedAt?: string;
    inReviewQuestionList?: InReviewQuestionListType;
    monetized?: boolean;
    isSeekerPreviouslyMonetized?: boolean;
  };
};

export const ReviewPausedQuestionModal = ({ isOpen, onClose, selectedToDoItem }: ReviewPausedQuestionModalProps): JSX.Element => {
  const { t } = useTranslation();
  const { approveRejectQuestions } = useStoreActions((action) => action.positionManagement);
  const [isApproveRejectLoading, setIsApproveRejectLoading] = useState(false);
  const [formData, setFormData] = React.useState<QuestionFormdataType>([]);
  const { updateRecruiterActionStatus } = useStoreActions((actions) => actions.toDoList);
  const userId = useStoreState((states) => states.app.user?.id);
  const [isAllQuestionsReviewed, setIsAllQuestionsReviewed] = useState(false);

  const { isOpen: isDismissModalOpen, onOpen: onDismissModalOpen, onClose: onDismissModalClose } = useDisclosure();

  const formDataSetterCallback = (givenFormData: QuestionFormdataType, givenIsAllQuestionsReviewed: boolean) => {
    setFormData(givenFormData);
    setIsAllQuestionsReviewed(givenIsAllQuestionsReviewed);
  };
  const candidatePositionId = selectedToDoItem?.candidateId.split('_')[0];
  const candidateSeekerId = selectedToDoItem?.candidateId.split('_')[1];

  const updateRecruiterAction = async () => {
    // istanbul ignore else
    if (userId && selectedToDoItem) {
      await updateRecruiterActionStatus({
        userId,
        docId: selectedToDoItem.id,
        status: 'COMPLETED',
        category: selectedToDoItem.task,
        subCategory: null,
        candidateId: selectedToDoItem.candidateId,
      });
    }
  };

  const handleApproveRejectQuestions = async () => {
    setIsApproveRejectLoading(true);
    try {
      await approveRejectQuestions({
        candidateId: /* istanbul ignore next */ selectedToDoItem?.candidateId ?? '',
        payload: formData,
      });
      setIsApproveRejectLoading(false);
    } catch (error) {
      setIsApproveRejectLoading(false);
    } finally {
      onClose();
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    updateRecruiterAction();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="3xl" scrollBehavior="inside">
      <ModalOverlay />

      <ModalContent position="absolute" transform="none" height="75vh" maxH="75vh" display="flex" flexDirection="column">
        <Box position="sticky" top="0" zIndex={10} bg="white" borderRadius="xl">
          <ModalHeader paddingLeft={6} pr="50px">
            <Text fontWeight="bold">{t('messaging:actions.questionReviewModal.title')}</Text>
          </ModalHeader>
          <ModalCloseButton position="absolute" top="16px" right="16px" zIndex={11} />
        </Box>
        <ModalBody borderTop={`1px solid ${colors.gray[100]}`} flexDirection="row" flex="1" height="100%">
          <Box flex={1} height="100%">
            <ReviewQuestions
              handleActionAfterEngage={
                // istanbul ignore next
                () => ''
              }
              inReviewQuestionList={selectedToDoItem?.inReviewQuestionList}
              formDataSetterCallback={formDataSetterCallback}
              renderedBy="ReviewPausedQuestionModal"
              isCandidateMonetized={selectedToDoItem?.monetized || selectedToDoItem?.isSeekerPreviouslyMonetized}
              givenPositionId={candidatePositionId}
              givenSeekerId={candidateSeekerId}
              onCloseReviewModal={() => {
                onClose();
              }}
              updateTodoList={() => updateRecruiterAction()}
            />
          </Box>
        </ModalBody>
        <ModalFooter justifyContent="flex-end">
          <Flex justifyContent="space-between" width="100%">
            <Button
              data-testid="CancelButton"
              colorScheme="red"
              mr={3}
              onClick={() => onClose()}
              disabled={isApproveRejectLoading}
            >
              {t('messaging:actions.hire.confirmationModal.cancelButton')}
            </Button>
            <Box display="flex">
              <Button
                variant="outline"
                colorScheme="red"
                fontSize={{ base: '9px', md: '11px', lg: '14px' }}
                mr="5px"
                onClick={() => onDismissModalOpen()}
                data-testid="dismissBtn"
              >
                {t('businessManagement:topCandidate.dismissCandidate')}
              </Button>

              {(selectedToDoItem?.monetized || selectedToDoItem?.isSeekerPreviouslyMonetized) && (
                <Button
                  data-testid="approveRejectButton"
                  isLoading={isApproveRejectLoading}
                  disabled={!isAllQuestionsReviewed || isApproveRejectLoading}
                  colorScheme="blue"
                  onClick={() => handleApproveRejectQuestions()}
                  ml={2}
                >
                  {t('messaging:actions.hire.confirmationModal.confirmBtn')}
                </Button>
              )}
              <Box id="topCandidateProfileAction" />
            </Box>

            {isDismissModalOpen && candidateSeekerId && candidatePositionId && (
              <DismissConfirmationModal
                isOpen={isDismissModalOpen}
                onClose={onDismissModalClose}
                positionId={candidatePositionId}
                seekerId={candidateSeekerId}
                handleActionAfterDismiss={() => {
                  // eslint-disable-next-line @typescript-eslint/no-floating-promises
                  updateRecruiterAction?.();
                  onClose();
                }}
              />
            )}
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
